const HOURS_PER_DAY = 24;
const MAX_HOUR = HOURS_PER_DAY - 1;
const MAX_MINUTE = 59;
const MINUTES_PER_HOUR = 60;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * MINUTES_PER_HOUR;
const MS_PER_DAY = MS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY;

const TZ_ADJUST_ITERATIONS = 3;
const DATE_KEY_YEAR_START = 0;
const DATE_KEY_YEAR_END = 4;
const DATE_KEY_MONTH_START = 5;
const DATE_KEY_MONTH_END = 7;
const DATE_KEY_DAY_START = 8;
const DATE_KEY_DAY_END = 10;
const MONTH_INDEX_OFFSET = 1;

const NOON_HOUR = 12;
const MAX_DEADLINE_SEARCH_DAYS = 14;
const TIMER_UPDATE_INTERVAL_SECONDS = 30;
const TIMER_UPDATE_INTERVAL_MS = TIMER_UPDATE_INTERVAL_SECONDS * MS_PER_SECOND;

const getZonedParts = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });

  const parts = {};
  formatter.formatToParts(date).forEach(({ type, value }) => {
    if (type !== 'literal') parts[type] = value;
  });

  let hour = Number(parts.hour);
  if (hour === HOURS_PER_DAY) hour = 0;

  return {
    weekday: parts.weekday?.toLowerCase(),
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    hour,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
};

const coerceCutoffString = (cutoff) => {
  if (cutoff == null || cutoff === false) return '';
  if (typeof cutoff === 'object') {
    return String(cutoff.value ?? cutoff.text ?? '').trim();
  }
  return String(cutoff).trim();
};

const parseCutoff = (cutoff) => {
  const raw = coerceCutoffString(cutoff);
  if (!raw) return null;
  let match = raw.match(/^(\d{1,2})[:.](\d{2})(?::\d{2})?$/);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > MAX_HOUR || minutes > MAX_MINUTE) return null;
    return { hours, minutes };
  }

  match = raw.match(/^(\d{1,2})[:.](\d{2})\s*(am|pm)$/i);
  if (match) {
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = match[3].toLowerCase();
    if (meridiem === 'pm' && hours < NOON_HOUR) hours += NOON_HOUR;
    if (meridiem === 'am' && hours === NOON_HOUR) hours = 0;
    if (hours > MAX_HOUR || minutes > MAX_MINUTE) return null;
    return { hours, minutes };
  }

  return null;
};

const zonedDateTimeToUtc = (dateKey, hours, minutes, timeZone) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  let utcGuess = Date.UTC(year, month - 1, day, hours, minutes, 0);

  for (let i = 0; i < TZ_ADJUST_ITERATIONS; i += 1) {
    const parts = getZonedParts(new Date(utcGuess), timeZone);
    const asUtc = Date.UTC(
      Number(parts.dateKey.slice(DATE_KEY_YEAR_START, DATE_KEY_YEAR_END)),
      Number(parts.dateKey.slice(DATE_KEY_MONTH_START, DATE_KEY_MONTH_END)) - MONTH_INDEX_OFFSET,
      Number(parts.dateKey.slice(DATE_KEY_DAY_START, DATE_KEY_DAY_END)),
      parts.hour,
      parts.minute,
      parts.second
    );
    const targetUtc = Date.UTC(year, month - 1, day, hours, minutes, 0);
    utcGuess += targetUtc - asUtc;
  }

  return new Date(utcGuess);
};

const addDaysToDateKey = (dateKey, days, timeZone) => {
  const noon = zonedDateTimeToUtc(dateKey, NOON_HOUR, 0, timeZone);
  const next = new Date(noon.getTime() + days * MS_PER_DAY);
  return getZonedParts(next, timeZone).dateKey;
};

/**
 * Next future cutoff (skips blank days + bank holidays).
 * Show only when that cutoff is today or tomorrow:
 */
const findNextDeadline = (now, config) => {
  const { timezone, cutoffs, bankHolidays = [] } = config;
  const holidaySet = new Set((bankHolidays || []).map(String));
  let dateKey = getZonedParts(now, timezone).dateKey;

  for (let dayOffset = 0; dayOffset < MAX_DEADLINE_SEARCH_DAYS; dayOffset += 1) {
    if (dayOffset > 0) {
      dateKey = addDaysToDateKey(dateKey, 1, timezone);
    }

    if (holidaySet.has(dateKey)) continue;

    const weekday = getZonedParts(zonedDateTimeToUtc(dateKey, NOON_HOUR, 0, timezone), timezone)
      .weekday;
    const cutoff = parseCutoff(cutoffs?.[weekday]);
    if (!cutoff) continue;

    const deadline = zonedDateTimeToUtc(dateKey, cutoff.hours, cutoff.minutes, timezone);
    if (now < deadline) return { deadline, dateKey };
  }

  return null;
};

const getVisibleDeadline = (now, config) => {
  const { timezone } = config;
  const found = findNextDeadline(now, config);
  if (!found) return null;

  const todayKey = getZonedParts(now, timezone).dateKey;
  const tomorrowKey = addDaysToDateKey(todayKey, 1, timezone);

  if (found.dateKey !== todayKey && found.dateKey !== tomorrowKey) {
    return null;
  }

  return found.deadline;
};

const formatDuration = (ms, labels) => {
  const totalMinutes = Math.max(0, Math.floor(ms / MS_PER_MINUTE));
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  const hourLabel = hours === 1 ? labels.hour : labels.hours;
  const minuteLabel = minutes === 1 ? labels.minute : labels.minutes;

  return `${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
};

const normalizeConfig = (config) => {
  const cutoffs = {};
  Object.entries(config.cutoffs || {}).forEach(([day, value]) => {
    cutoffs[String(day).toLowerCase()] = coerceCutoffString(value) || null;
  });

  return {
    ...config,
    cutoffs,
    bankHolidays: (config.bankHolidays || []).map(String),
  };
};

const readConfig = (root) => {
  const configEl = root.querySelector('[data-next-day-delivery-config]');
  if (!configEl) return null;

  try {
    return normalizeConfig(JSON.parse(configEl.textContent));
  } catch (error) {
    console.error('Failed to parse next-day delivery config:', error);
    return null;
  }
};

const initProductNextDayDelivery = (scope = document) => {
  scope.querySelectorAll('[data-product-next-day-delivery]').forEach(root => {
    if (root.dataset.productNextDayDeliveryInit === 'true') return;

    const config = readConfig(root);
    if (!config?.timezone || !config.cutoffs) return;

    const timerEl = root.querySelector('[data-next-day-timer]');
    const orderWithinEl = root.querySelector('[data-next-day-order-within]');
    const shippingMessageEl = root.querySelector('[data-next-day-shipping-message]');
    if (!timerEl) return;

    if (orderWithinEl && config.orderWithinText) {
      orderWithinEl.textContent = config.orderWithinText;
    }
    if (shippingMessageEl && config.shippingMessage) {
      shippingMessageEl.textContent = config.shippingMessage;
    }

    root.dataset.productNextDayDeliveryInit = 'true';

    const hide = () => {
      root.hidden = true;
      timerEl.textContent = '--';
    };

    const update = () => {
      const now = new Date();
      const deadline = getVisibleDeadline(now, config);

      if (!deadline) {
        hide();
        return;
      }

      root.hidden = false;
      timerEl.textContent = formatDuration(deadline.getTime() - now.getTime(), config.labels);
    };

    update();
    const intervalId = window.setInterval(update, TIMER_UPDATE_INTERVAL_MS);

    root.addEventListener(
      'product-next-day-delivery:destroy',
      () => {
        window.clearInterval(intervalId);
      },
      { once: true }
    );
  });
};

export default initProductNextDayDelivery;
